import styles from "./CardSizeToggle.module.scss";
import { GalleryViewOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GalleryViewOutlined";
import { TileViewOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TileViewOutlined";
import classNames from "classnames/bind";
import { GLOBAL_INTERACTION, PLP_VIEW_TYPE_SELECTED } from "@/utils/events";
import { dispatchAnalyticsEvent } from "@/utils/analytics";
import { useCallback, useEffect, useRef } from "react";
import { useRouteParams } from "@/hooks/useRouteParams";
import { useFeatureFlags } from "@/flags/flags";
import { StandardProps } from "@/types/types";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { LinkWithoutPrefetch } from "@/components/__shared__/LinkWithoutPrefetch/LinkWithoutPrefetch";

const cx = classNames.bind(styles);

export function CardSizeToggle({
  configProps,
}: Pick<StandardProps, "configProps">) {
  const route = useRouteParams();
  const flags = useFeatureFlags();

  const firstProductIdInView = useRef<string | undefined>(undefined);

  const queryParams = route.query;

  const isLargeCardSize = queryParams.cardSize
    ? queryParams.cardSize === "large"
    : flags.isLargeCardSizeFlagOn.value;

  const cardSize = isLargeCardSize ? "small" : "large";

  const handleToggle = useCallback(() => {
    firstProductIdInView.current = getFirstInViewProduct();

    dispatchAnalyticsEvent(GLOBAL_INTERACTION, {
      action: GLOBAL_INTERACTION,
      type: "plp",
      subType: "card size toggle",
      additionalInfo: { size: !isLargeCardSize ? "large" : "small" },
      properties: {},
      products: [{}],
    });

    dispatchAnalyticsEvent(PLP_VIEW_TYPE_SELECTED, {
      plpViewType: !isLargeCardSize ? "large" : "small",
    });
    // TODO: find out if this is still required
    // const monetateEvent = `plp.cardsize.${cardSize}.${name}${name === "inControls" && !inView ? ".scrolling" : ""}`;
    // sendMonetateQEvent(monetateEvent);
  }, [cardSize]);

  useEffect(() => {
    if (firstProductIdInView.current) {
      scrollProductIntoView(firstProductIdInView.current);
      firstProductIdInView.current = undefined;
    }
  }, [cardSize]);

  if (!flags.isLargeCardSizeFlagOn.value) {
    return null;
  }

  return (
    <LinkWithoutPrefetch
      href={{
        pathname: route.path,
        query: sanitiseQueryParams({ ...queryParams, cardSize }),
      }}
      data-component="CardSizeToggle"
      data-testid="card-size-toggle"
      className={cx("button", {
        "button--on": isLargeCardSize,
        "button--desktop-only": configProps?.desktopOnly,
        "button--mobile-only": configProps?.mobileOnly,
        "button--icon-only": configProps?.iconOnly,
      })}
      scroll={false}
      onClick={handleToggle}
      shallow
    >
      <span className={styles.buttonSection}>
        {!configProps?.iconOnly ? "Small" : ""}{" "}
        <TileViewOutlined size="medium" />
      </span>
      <span className={styles.buttonSection}>
        {!configProps?.iconOnly ? "Large" : ""}{" "}
        <GalleryViewOutlined size="medium" />
      </span>
    </LinkWithoutPrefetch>
  );
}

const getFirstInViewProduct = (): string | undefined => {
  const bottomOfControls =
    document.querySelector('[data-slot="sticky-bar"]')?.getBoundingClientRect()
      .bottom || 0;

  const productCards = document.querySelectorAll(
    "[data-product-card-layout][data-product-id]",
  );

  const firstCardInView = Array.from(productCards).find(
    (el) => (el as HTMLElement).getBoundingClientRect().top >= bottomOfControls,
  );

  if (firstCardInView) {
    return (firstCardInView as HTMLElement)?.dataset.productId;
  } else {
    const lastCard = productCards[productCards.length - 1];
    return (lastCard as HTMLElement)?.dataset.productId;
  }
};

const scrollProductIntoView = (productId: string) => {
  const bottomOfControls =
    document.querySelector('[data-slot="sticky-bar"]')?.getBoundingClientRect()
      .bottom || 0;

  const card = document.querySelector(
    `[data-product-card-layout][data-product-id="${productId}"]`,
  );

  window.scrollTo(
    0,
    (card?.getBoundingClientRect().top || 0) +
      window.scrollY -
      (bottomOfControls + 24),
  );
};
